import { useKidRoutes } from '@entities/customer'
import { useCustomerStore } from '@entities/customer'
import { useDisciplineStore } from '@entities/customer'
import { useDiscipline, useRawDiscipline } from '@entities/customer'
import { isCompletedBooking } from '@shared/lib/booking-status'
import { trackEvent } from '@shared/lib/amplitude'

interface BtnPlansPayload {
  query?: Record<string, string>
  text?: string | null
}

interface BtnPlansReturn {
  text: string
  selected: ComputedRef<boolean>
  disabled: ComputedRef<boolean>
  plansUrl: ComputedRef<string | null>
  setRedirect: () => Promise<void>
}

export const useBtnPlans = (payload: BtnPlansPayload): BtnPlansReturn => {
  const customerStore = useCustomerStore()
  const disciplineStore = useDisciplineStore()

  const {
    isDisciplineEla,
  } = useDiscipline({ discipline: disciplineStore.getDisciplineCode })

  const {
    padMath,
    padEla,
    pricingMath,
    pricingEla,
    isRoutePad,
    isRoutePricing,
  } = useKidRoutes()

  const text = payload.text || 'Check prices'

  const selected = computed(() => isRoutePad.value || isRoutePricing.value)
  const disabled = computed(() => !customerStore.isSomeDisciplineDemoCompleted)

  const plansMath = computed(() => customerStore.isTypePaid ? pricingMath.value : padMath.value)
  const plansEla = computed(() => customerStore.isTypePaid ? pricingEla.value : padEla.value)

  const plansUrl = computed(() => {
    if (customerStore.isAllDisciplinesDemoCompleted) {
      return isDisciplineEla.value
        ? plansEla.value
        : plansMath.value
    } else {
      const kid = customerStore.getKidInCustomer
      const disciplineWithCompletedDemo = kid?.disciplines.find(discipline => isCompletedBooking(discipline?.demo || null)) || null
      if (!disciplineWithCompletedDemo) {
        return null
      } else {
        const ela = useRawDiscipline({ discipline: disciplineWithCompletedDemo.code }).isDisciplineEla
        return ela
          ? plansEla.value
          : plansMath.value
      }
    }
  })

  const setRedirect = async (): Promise<void> => {
    // `notify_type` here names the placement (`dashboard_stepper`, `skill_report`, …), not a notification.
    const placement = payload.query?.notify_type

    trackEvent('Button_click', {
      feature: placement?.startsWith('dashboard') ? 'dashboard' : placement === 'skill_report' ? 'skill_report' : 'plans',
      name: placement ? `plans_${placement}` : 'button_plans',
      text,
      url: plansUrl.value,
    })

    if (plansUrl.value) {
      await navigateTo({
        path: plansUrl.value,
        query: payload.query || {},
      })
    }
  }

  return {
    text,
    selected,
    disabled,
    plansUrl,
    setRedirect,
  }
}
